// AdminPage.js
import React, { useState, useEffect, useRef } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import s from "./AdminPage.module.css";
import { FiChevronDown, FiChevronUp } from "react-icons/fi";
import ReactDOM from "react-dom";

const DropdownMenu = ({ open, anchorRef, children, menuRef }) => {
  const [coords, setCoords] = useState(null);
  useEffect(() => {
    if (open && anchorRef.current) {
      const rect = anchorRef.current.getBoundingClientRect();
      setCoords({
        top: rect.bottom,
        left: rect.left,
        width: rect.width,
      });
    }
  }, [open, anchorRef]);
  if (!open || !coords) return null;
  return ReactDOM.createPortal(
    <div
      className={s.dropdownMenu}
      ref={menuRef}
      style={{
        position: "fixed",
        top: coords.top,
        left: coords.left,
        minWidth: coords.width,
        zIndex: 9999,
      }}
    >
      {children}
    </div>,
    document.body
  );
};

const AdminPage = () => {
  const [openDropdown, setOpenDropdown] = useState(null);
  const [waitingCount, setWaitingCount] = useState(0);
  const location = useLocation();
  const usersRef = useRef(null);
  const balanceRef = useRef(null);
  const dropdownMenuRef = useRef(null);

  useEffect(() => {
    setOpenDropdown(null);
  }, [location.pathname]);

  useEffect(() => {
    fetchWaitingCount();
  }, []);

  const fetchWaitingCount = async () => {
    try {
      const response = await fetch("/api/users/waiting/count");
      const data = await response.json();
      setWaitingCount(data.count);
    } catch (error) {
      setWaitingCount(0);
    }
  };

  useEffect(() => {
    if (!openDropdown) return;
    function handleClickOutside(event) {
      if (
        (usersRef.current && usersRef.current.contains(event.target)) ||
        (balanceRef.current && balanceRef.current.contains(event.target))
      ) {
        // Клик по кнопке — не закрываем
        return;
      }
      if (
        dropdownMenuRef.current && dropdownMenuRef.current.contains(event.target)
      ) {
        // Клик по самому меню — не закрываем
        return;
      }
      setOpenDropdown(null);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openDropdown]);

  const handleDropdown = (menu) => {
    setOpenDropdown(openDropdown === menu ? null : menu);
  };

  // Функция для обработки активного состояния NavLink
  const getNavLinkClass = (navClass) => ({ isActive }) =>
      isActive ? `${navClass} ${s.active}` : navClass;

  return (
      <div className={s.adminWrapper}>
        <nav className={s.adminNavbar}>
          <div className={s.navContainer}>
            <NavLink to="/admin" className={s.navBrand}>
              Админ-панель
            </NavLink>

            <div className={s.navItems}>
              {/* Выпадающее меню пользователей */}
              <div
                  className={s.navItem}
                  ref={usersRef}
                  onClick={() => handleDropdown('users')}
              >
                <span>👥 Пользователи</span>
                {waitingCount > 0 && (
                  <span className={s.waitingBadge}>{waitingCount}</span>
                )}
                {openDropdown === 'users' ? <FiChevronUp /> : <FiChevronDown />}
              </div>
              <DropdownMenu open={openDropdown === 'users'} anchorRef={usersRef} menuRef={dropdownMenuRef}>
                <NavLink
                    to="accept-members"
                    className={getNavLinkClass(s.dropdownItem)}
                >
                  Ожидающие вступления
                  {waitingCount > 0 && (
                    <span className={s.waitingBadge}>{waitingCount}</span>
                  )}
                </NavLink>
                <NavLink
                    to="members"
                    className={getNavLinkClass(s.dropdownItem)}
                >
                  Члены организации
                </NavLink>
                <NavLink
                    to="access-management"
                    className={getNavLinkClass(s.dropdownItem)}
                >
                  Доступы
                </NavLink>
              </DropdownMenu>

              {/* Стандартные пункты меню */}
              <NavLink
                  to="print-docs"
                  className={getNavLinkClass(s.navItem)}
              >
                📄 Печать документов
              </NavLink>

              <NavLink
                  to="events"
                  className={getNavLinkClass(s.navItem)}
              >
                📅 События
              </NavLink>

              <NavLink
                  to="organizations"
                  className={getNavLinkClass(s.navItem)}
              >
                🏢 Организации
              </NavLink>

              <NavLink
                  to="books"
                  className={getNavLinkClass(s.navItem)}
              >
                📚 Управление книгами
              </NavLink>

              <NavLink
                  to="news"
                  className={getNavLinkClass(s.navItem)}
              >
                📰 Управление новостями
              </NavLink>

              {/* Уникальные пункты из второй версии */}
              <div
                  className={s.navItem}
                  ref={balanceRef}
                  onClick={() => handleDropdown('balance')}
              >
                <span>💰 Баланс</span>
                {openDropdown === 'balance' ? <FiChevronUp/> : <FiChevronDown/>}
              </div>
              <DropdownMenu open={openDropdown === 'balance'} anchorRef={balanceRef} menuRef={dropdownMenuRef}>
                <NavLink
                    to="contributions"
                    className={getNavLinkClass(s.dropdownItem)}
                >
                  💰 Взносы
                </NavLink>
                <NavLink
                    to="expenses"
                    className={getNavLinkClass(s.dropdownItem)}
                >
                  💸 Расходы
                </NavLink>
              </DropdownMenu>
            </div>
          </div>
        </nav>

        <main className={s.adminContent}>
          <Outlet/>
        </main>
      </div>
  );
};

export default AdminPage;